/**
 * 設定 → 退会（縦串7、026、docs/21。射場さん 9/17 の決定）
 *   1. 何が起きるか（押した瞬間にログイン不可 → 30日は元に戻せる → 30日後に消える）
 *   2. 「データを持ち出す」（出した見積書・請求書の PDF と一覧の CSV を zip に。請求書の控えは 7年保存が使う側の義務）
 *   3. アンケート（任意）：製品内容（いくつでも）・移行先（1つ。製品内容で「他社の製品に移る」のときだけ）・退会理由（自由記述）
 *   4. いまのパスワード ＋「消えることを分かったうえで退会する」のチェック → 「退会」
 *   5. 受け付けたら、その端末もログアウト。/login に「退会を受け付けました」の案内
 * #156（docs/65 §9、Figma「設定の残り（10/9）」㉘）で節に並べ直した（中身・決まりは docs/21 のまま）：
 *   節「退会するとどうなるか」（番号つき3行。バックアップの1文は節の注）／「データを持ち出す」（zip の行 › ダウンロード）／
 *   「アンケート」（2026-11-01、#285 の続き：製品内容 9行・移行先 8行・退会理由の欄）／「確かめ」（いまのパスワード・確かめの ✓）／「退会」（朱）。
 *   パスワードと ✓ がそろうまで「退会」は押せない。今の「取消（退会しない）」は上の帯の「‹ 設定」で足りるのでやめた
 * #189（docs/76 §0.3 ク）：パスワードの無い人（Google・Apple・パスキーだけ）はパスワードの欄を出さず、「退会」で本人確認の窓（API の 403 REAUTH_REQUIRED）
 */
import { ACCOUNT_MSG, CLOSE_DESTINATIONS, CLOSE_DESTINATION_LABEL, CLOSE_GRACE_DAYS, CLOSE_REASON_LABEL, CloseReason, REST_MSG, type CloseDestination, type CloseResult, type ReauthStatus } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { setMe } from "../auth.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { Chevron, GroupCheckRow, GroupDangerRow, GroupInputRow, GroupList, GroupSection } from "../components/GroupList.jsx";
import { SettingsHead, useRestPageClass } from "../components/SettingsHead.jsx";
import { ErrorText, Help, guardComposingEnter } from "../components/ui.jsx";
import { PASSIVE } from "../lib/autoLogout.js";
import { useLabels } from "../labels.js";
import { currentProduct } from "../lib/product.js";

const REASONS = CloseReason.options;

export function SettingsClosePage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const L = useLabels();
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ 設定
  const back = useBackNav({ to: "/settings", label: "設定" });
  usePageName(REST_MSG.closeTitle);
  const cls = useRestPageClass("closepage");
  const [reasons, setReasons] = useState<CloseReason[]>([]);
  const [destination, setDestination] = useState<CloseDestination | null>(null);
  const [reasonText, setReasonText] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [exported, setExported] = useState(false);

  const toggle = (r: CloseReason) => {
    setReasons((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
    // 「他社の製品に移る」を外したら移行先も空にする（移行先はそのときだけ出す）
    if (r === "other_service" && reasons.includes(r)) setDestination(null);
  };
  const movesTo = reasons.includes("other_service");
  const destinations = CLOSE_DESTINATIONS[currentProduct()];
  // パスワードのある人か（#189：無い人は本人確認の窓で確かめる）
  const reauth = useQuery({ queryKey: ["reauth-status"], queryFn: () => api<ReauthStatus>("GET", "/auth/reauth-status", undefined, PASSIVE), staleTime: 0 });
  const hasPassword = reauth.data?.methods.password !== false;
  const ready = (!hasPassword || !!password) && confirm;

  const submit = async () => {
    setError(undefined);
    if (hasPassword && !password) {
      setError(ACCOUNT_MSG.passwordRequired);
      document.getElementById("cl_pw")?.focus();
      return;
    }
    if (!confirm) {
      setError(ACCOUNT_MSG.confirmRequired);
      document.getElementById("cl_confirm")?.focus();
      return;
    }
    setBusy(true);
    try {
      const r = await api<CloseResult>("POST", "/account/close", { password, confirm: true, reasons, destination: movesTo ? destination : null, reasonText });
      qc.clear();
      await setMe(qc, null);
      nav("/login", { replace: true, state: { closed: { purgeAfter: r.purgeAfter, email: r.email } } });
    } catch (e) {
      const first = e instanceof ApiError && e.problem.errors ? (Object.values(e.problem.errors)[0] as string | undefined) : undefined;
      setError(first ?? messageOf(e));
      setBusy(false);
      if (e instanceof ApiError && e.code === "ACCOUNT_PASSWORD_WRONG") document.getElementById("cl_pw")?.focus();
    }
  };

  return (
    <div className={cls} data-testid="close">
      <SettingsHead back={back} title={REST_MSG.closeTitle} />
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (ready && !busy) void submit();
        }}
      >
        <GroupSection title={REST_MSG.closeSectionWhat} note={REST_MSG.closeBackupNote} data-testid="close-sec-what">
          <ol className="glist glist-plain close-steps">
            <li className="grow grow-set grow-text">
              <div className="growin">
                <span className="close-no">1</span>
                <span className="gtxt">「退会」を押した瞬間に、ログインできなくなります（ほかの端末も）。</span>
              </div>
            </li>
            <li className="grow grow-set grow-text">
              <div className="growin">
                <span className="close-no">2</span>
                <span className="gtxt">
                  <b>{CLOSE_GRACE_DAYS}日のあいだは元に戻せます。</b>受け付けのメールにあるリンクを押すと、今までどおり使えます。
                </span>
              </div>
            </li>
            <li className="grow grow-set grow-text">
              <div className="growin">
                <span className="close-no">3</span>
                <span className="gtxt">
                  {CLOSE_GRACE_DAYS}日たつと、見積書・請求書・{L.customer}・社印などのデータを<b>すべて消します</b>。戻せません。メールアドレスの登録も消えるので、同じメールで新しく登録し直せます（データは白紙から）。
                </span>
              </div>
            </li>
          </ol>
        </GroupSection>

        <GroupSection
          title={REST_MSG.closeSectionExport}
          help={<Help testId="help-export" text={`出した見積書・請求書の PDF と、見積書・請求書・明細・${L.customer}の一覧（CSV。Excel で開けます）を1つの zip でダウンロードします。出した請求書の控えは、法律（電子帳簿保存法）で 7年の保存が必要です。退会の前に必ず持ち出してください。`} />}
          note={exported ? <span id="exportStarted">{REST_MSG.closeExportStarted}</span> : REST_MSG.closeExportNote}
          data-testid="close-sec-export"
        >
          <GroupList plain>
            <li className="grow grow-set grow-nav">
              <a className="growin" href="/api/v1/account/export" onClick={() => setExported(true)} data-testid="close-export">
                <span className="gtx">
                  <span className="gtt">{REST_MSG.closeExport}</span>
                </span>
                <span className="gend">
                  <span className="gval">{REST_MSG.closeExportValue}</span>
                  <Chevron />
                </span>
              </a>
            </li>
          </GroupList>
        </GroupSection>

        <GroupSection title={REST_MSG.closeSectionReasons} help={<Help testId="help-reasons" text="製品内容はいくつでも選べます。答えなくても退会できます。" />} note={REST_MSG.closeReasonsNote} data-testid="close-sec-reasons">
          <div className="close-qgroup" role="group" aria-labelledby="close-q-product" data-testid="close-q-product">
            <p className="close-q" id="close-q-product">{REST_MSG.closeQuestionProduct}</p>
            <GroupList plain>
              {REASONS.map((r) => (
                <GroupCheckRow key={r} multi title={CLOSE_REASON_LABEL[r]} checked={reasons.includes(r)} onChange={() => toggle(r)} buttonTestId={`close-reason-${r}`} />
              ))}
            </GroupList>
          </div>
          {movesTo ? (
            <div className="close-qgroup" role="radiogroup" aria-labelledby="close-q-destination" data-testid="close-q-destination">
              <p className="close-q" id="close-q-destination">{REST_MSG.closeQuestionDestination}</p>
              <GroupList plain>
                {destinations.map((d) => (
                  <GroupCheckRow key={d} radioName="close-destination" title={CLOSE_DESTINATION_LABEL[d]} checked={destination === d} onChange={() => setDestination(d)} buttonTestId={`close-destination-${d}`} />
                ))}
              </GroupList>
            </div>
          ) : null}
          <GroupList plain className="close-q-text">
            <li className="grow grow-set grow-area">
              <label className="growin">
                <span className="gilbl" id="cl_text_label">{REST_MSG.closeReasonText}</span>
                {/* 欄の名前は「退会理由」だけ（下の注記は説明＝aria-describedby。label の中の文字を全部名前にしない） */}
                <textarea id="cl_text" className="giarea" rows={3} maxLength={1000} value={reasonText} onChange={(e) => setReasonText(e.target.value)} aria-labelledby="cl_text_label" aria-describedby="cl_text_note" />
                <span className="close-fieldnote" id="cl_text_note" data-testid="close-text-note">{REST_MSG.closeReasonTextNote}</span>
              </label>
            </li>
          </GroupList>
        </GroupSection>

        <GroupSection title={REST_MSG.closeSectionConfirm} data-testid="close-sec-confirm">
          <GroupList plain>
            {hasPassword ? (
              <GroupInputRow id="cl_pw" label={REST_MSG.closePassword} wide>
                <input
                  id="cl_pw"
                  className="giinp"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onKeyDown={guardComposingEnter}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={error === ACCOUNT_MSG.passwordWrong || error === ACCOUNT_MSG.passwordRequired}
                />
              </GroupInputRow>
            ) : null}
            <GroupCheckRow multi inputId="cl_confirm" title={REST_MSG.closeConfirm.replace("{days}", String(CLOSE_GRACE_DAYS))} sub={REST_MSG.closeConfirmSub} checked={confirm} onChange={setConfirm} className="close-confirm" />
          </GroupList>
        </GroupSection>
        <ErrorText>{error}</ErrorText>

        <GroupSection note={REST_MSG.closeButtonNote} data-testid="close-sec-submit">
          <GroupList plain>
            <GroupDangerRow title={busy ? REST_MSG.closeBusy : REST_MSG.closeButton} disabled={!ready || busy} buttonTestId="close-account" onClick={() => void submit()} />
          </GroupList>
        </GroupSection>
      </form>
    </div>
  );
}
